import { Path, Svg } from '../../svg';
import { useTheme } from '../../theme';
import { mixHex, tintHex } from '../../utils/color';

/**
 * The folder back — a manila-folder shape (rounded body + a tab on the
 * top-left). Filled with the folder's identity `color`; the 1px outline is a
 * slightly darker shade of the same colour (Figma pairs red-400 fill /
 * red-500 stroke — approximated as `color` mixed 12% toward black so any
 * folder colour works). `preserveAspectRatio` is `none` so the shape stretches
 * to fill the tile box — which is why each grid subvariant needs its OWN
 * path: the compact one blown up to the expanded 252×192 box came out 1.5×
 * wide, flattening the tab and the bottom corners.
 *
 * compact  — Figma "Cards Updated" 3153:43604 (`Vector 1779`, 168×181)
 * expanded — Figma "Folders Updated" 5656:17087 (`Vector 1779`, 258×195)
 */
const SHAPE = {
  compact: {
    viewBox: '0 0 168 181',
    path:
      'M24 0.5H58.5039C63.8835 0.5 68.9613 2.98636 72.2607 7.23535C75.7496 ' +
      '11.7281 81.1193 14.3564 86.8076 14.3564H144C156.979 14.3564 167.5 ' +
      '24.8778 167.5 37.8564V157C167.5 169.979 156.979 180.5 144 180.5H24C11.0213 ' +
      '180.5 0.5 169.979 0.5 157V24C0.5 11.0213 11.0213 0.5 24 0.5Z',
  },
  expanded: {
    viewBox: '0 0 258 195',
    path:
      'M24 0.5H92.9912C99.0437 0.500089 104.504 4.13514 106.84 9.71875C109.331 ' +
      '15.6741 115.156 19.5506 121.611 19.5508H234C246.979 19.5508 257.5 ' +
      '30.0721 257.5 43.0508V171C257.5 183.979 246.979 194.5 234 194.5H24C11.0213 ' +
      '194.5 0.5 183.979 0.5 171V24C0.500008 11.0213 11.0213 0.500001 24 0.5Z',
  },
} as const;

export function FolderShape({
  color,
  size = 'compact',
}: {
  color: string;
  size?: 'compact' | 'expanded';
}) {
  const { viewBox, path } = SHAPE[size];
  return (
    <Svg
      width="100%"
      height="100%"
      viewBox={viewBox}
      preserveAspectRatio="none"
      fill="none">
      <Path
        d={path}
        fill={color}
        stroke={mixHex(color, 'black', 0.12)}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </Svg>
  );
}

/**
 * Subfolder PREVIEW shape — the mini folder drawn inside a FolderCard /
 * StudyGroupCard stack (Figma 5686:88589 expanded / 5686:88907 compact /
 * 5686:89162 list: one master vector at three exports, so the tab and radii
 * keep their proportions per variant instead of stretching one path). Colour
 * treatment differs from the folder back: the fill is the −300 tone (the
 * identity −400 mixed 40% toward white — red-300/red-400 in the Figma
 * sample) and the 1px stroke is the −500 accent. The viewBox min-x/min-y
 * crop the export's drop-shadow padding — coordinates stay exactly as
 * exported.
 */
const PREVIEW_SHAPE = {
  // List row thumb (Figma 5726:91491 `Vector 1781` — the A4-format 55×78
  // export).
  list: {
    viewBox: '3.82383 2.86788 55 78',
    path:
      'M3.82383 11.7605V27.8513V71.9752C3.82383 76.8865 7.8052 80.8679 ' +
      '12.7165 80.8679H49.9312C54.8425 80.8679 58.8238 76.8865 58.8238 ' +
      '71.9753V18.0009C58.8238 13.0896 54.8425 9.10823 49.9312 ' +
      '9.10823H39.7311C37.1318 9.10823 34.6635 7.96761 32.979 ' +
      '5.98805C31.2945 4.0085 28.8261 2.86788 26.2268 2.86788H12.7165C7.8052 ' +
      '2.86788 3.82383 6.84924 3.82383 11.7605Z',
  },
  compact: {
    viewBox: '5.83491 4.37618 145.873 110.253',
    path:
      'M5.83491 17.9457V41.1313V101.059C5.83491 108.554 11.9102 114.629 ' +
      '19.4045 114.629H138.138C145.632 114.629 151.708 108.554 151.708 ' +
      '101.059V28.7171C151.708 21.2228 145.632 15.1475 138.138 ' +
      '15.1475H74.594C71.0579 15.1475 67.8676 13.024 66.5029 9.76185C65.1382 ' +
      '6.4997 61.948 4.37618 58.4119 4.37618H19.4045C11.9102 4.37618 5.83491 ' +
      '10.4515 5.83491 17.9457Z',
  },
  expanded: {
    viewBox: '8 6 200 151.163',
    path:
      'M8 24.6046V56.3934V138.558C8 148.833 16.3296 157.163 26.6046 ' +
      '157.163H189.395C199.67 157.163 208 148.833 208 ' +
      '138.558V39.3728C208 29.0977 199.67 20.7681 189.395 ' +
      '20.7681H102.273C97.4245 20.7681 93.0505 17.8567 91.1794 ' +
      '13.3841C89.3082 8.91147 84.9343 6 80.086 6H26.6047C16.3296 6 8 ' +
      '14.3296 8 24.6046Z',
  },
} as const;

/**
 * Shrinks the stroked outline toward the viewBox centre by ~0.5 user units
 * per side, so the 1px non-scaling stroke hugs the inside of every edge —
 * the CardBodyPanel rim technique, generalised for a viewBox whose
 * min-x/min-y aren't 0. (The Figma exports DO ship their own inset stroke
 * paths, but their inset is baked at the export's scale — the list export's
 * 0.19-unit inset left most of the 1px stroke outside the fill, where the
 * viewport shaved it thin on the edges it rides.)
 */
function insetStrokeTransform(viewBox: string) {
  const [mx, my, w, h] = viewBox.split(' ').map(Number);
  const sx = (w - 1) / w;
  const sy = (h - 1) / h;
  return `translate(${mx * (1 - sx) + 0.5} ${my * (1 - sy) + 0.5}) scale(${sx} ${sy})`;
}

export function FolderPreviewShape({
  color,
  size = 'compact',
}: {
  /** The subfolder's identity colour (−400 hex): fill −300, stroke −500. */
  color: string;
  size?: 'compact' | 'expanded' | 'list';
}) {
  const { viewBox, path } = PREVIEW_SHAPE[size];
  const { isDark } = useTheme();
  return (
    <Svg
      width="100%"
      height="100%"
      viewBox={viewBox}
      preserveAspectRatio="none"
      fill="none">
      <Path d={path} fill={tintHex(color, 0.4, isDark)} />
      {/* −500 accent stroke for contrast against the −300 fill — same
          12%-black approximation of the −400→−500 step the folder back
          uses, so any folder colour works. */}
      <Path
        d={path}
        transform={insetStrokeTransform(viewBox)}
        fill="none"
        stroke={mixHex(color, 'black', 0.12)}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </Svg>
  );
}
